import React, { useCallback } from 'react';
import ToggleButton from './ToggleButton';
import { CONTACT_US } from '../data/Localization';
import { formatStorePrice } from '../../../utils/ecommerce/currency';

/**
 * Represents a toggle button for a product card.
 * @param {Object} props - The component props.
 * @param {boolean} props.selected - Indicates if the toggle button is selected.
 * @param {Function} props.onClick - The click event handler for the toggle button.
 * @param {Object} props.customAttributes - Additional custom attributes for the toggle button.
 * @param {string} props.tag - The tag for the toggle button.
 * @param {string} props.label - The label for the toggle button.
 * @returns {JSX.Element} The toggle button component.
 */
const CardToggle = (props) => {
	return (
		<ToggleButton
			className="prc__cta-yr"
			selected={props.selected}
			onClick={() => props.onClick(props.sku)}
			customAttributes={{
				...props.customAttributes,
			}}>
			<span className="prc__cta-tag no-call">{props.tag}</span>
			<span className="prc__cta-price no-call">{props.label}</span>
		</ToggleButton>
	);
};

/**
 * Represents a group of product toggle buttons.
 * @param {Object} props - The component props.
 * @param {Array} props.products - The array of products to display toggle buttons for.
 * @param {string} props.activeSku - The SKU of the currently active product.
 * @param {Function} props.onToggleClick - The click event handler for the toggle buttons.
 * @param {string} props.formattedLocale - The formatted locale for displaying prices.
 * @param {string} props.currency - The currency for displaying prices.
 * @returns {JSX.Element} The product toggle group component.
 */
const ProductToggleGroup = (props) => {
	const hasValidPrice = (product) => {
		return typeof product.amount === 'number' && !isNaN(product.amount);
	};

	const formatPrice = useCallback(
		(price) => {
			const isWholeNumber = price % 1 === 0;
			const currencyOptions = isWholeNumber ? { maximumFractionDigits: 0 } : undefined;
			return formatStorePrice(price, props.formattedLocale, props.currency, currencyOptions);
		},
		[props.formattedLocale, props.currency]
	);

	const getLabelName = (product) => {
		if (product.isPurchasable && hasValidPrice(product)) {
			return `${formatPrice(product.amount)}${product.renewalTitle || ''}`;
		} else if (product.isFree === 'true') {
			return product.noPriceLabel;
		} else {
			return CONTACT_US;
		}
	};

	const getAttributes = (product) => {
		return {
			'data-sku': product.sku,
			...(product.isPurchasable && { 'data-click': 'handlePriceChange' }),
			...(!product.isPurchasable && { 'data-isfree': product.isFree }),
		};
	};

	return (
		<div className="prc__card--cta-wrapper">
			{props.products?.length > 1 &&
				props.products.map((product, indx) => {
					if (!product.sku) {
						console.error('ERROR: [Product with no SKU]', product);
						return null;
					}
					if (product.isPurchasable && !hasValidPrice(product)) {
						console.error('ERROR: [product is purchasable but no display price]', product);
						return null;
					}
					if (!product.isPurchasable && !(product.isFree === 'true' || product.isFree === 'false')) {
						return null;
					}

					return (
						<CardToggle
							key={indx}
							selected={props.activeSku === product.sku}
							sku={product.sku}
							onClick={props.onToggleClick}
							tag={product.tag}
							label={getLabelName(product)}
							customAttributes={getAttributes(product)}
						/>
					);
				})}
		</div>
	);
};

export default ProductToggleGroup;
